import { DateOnly } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { ControlledDateOnlyPicker, ControlledSelect, ErrorAlert } from '@if/mui'
import { Alert, Stack, Typography } from '@mui/material'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUser } from '@src/providers/UserProvider'
import { useUnmountableDialog } from '@src/services/dialog'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { EPaymentMethod, useTranslatedPaymentFrequency } from '@src/types/constants'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import LoanCalendarActionPreviewTable from '../LoanCalendarActionPreview/LoanCalendarActionPreviewTable'
import { useShouldDisableDateForNewFirstPayment } from './LoanCalendarFrequency-hooks'
import { NewFrequencySchemaType, useNewFrequencyResolver } from './LoanCalendarFrequency-schema'
import { getLoanCalendarMaxPaymentDate } from './LoanCalendarFrequency-selectors'

interface LoanCalendarFrequencyDialogProps {
  initialSignedSimulation: SignedCalendarSimulation
  onConfirm: (data: SignedCalendarSimulation) => void
  onClose: () => void
  isLockedByOther?: boolean
}
const LoanCalendarFrequencyDialog = ({
  initialSignedSimulation,
  onConfirm,
  onClose,
  isLockedByOther = false,
}: LoanCalendarFrequencyDialogProps) => {
  const { t } = useTranslation()
  const [newSimulationWithSignature, setNewSimulationWithSignature] =
    useState<SignedCalendarSimulation>(initialSignedSimulation)

  const {
    mutateAsync: computeAction,
    error,
    isPending,
  } = useComputeCalendarAction(
    initialSignedSimulation.simulation.fullLoan.loan.id,
    initialSignedSimulation.simulation.fullLoan.loan.financingProgramId,
  )

  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)
  const frequencyOptions = useTranslatedPaymentFrequency()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const loanActivationDate = initialSignedSimulation.simulation.fullLoan.loan.activationOn
  const minFirstPaymentDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(
        EPaymentMethod.PreAuthorizedPayment,
        loanActivationDate,
        canBypassAcpLock,
      ),
    [canBypassAcpLock, loanActivationDate],
  )
  const resolver = useNewFrequencyResolver()

  const currentPaymentConfig = useMemo(
    () => initialSignedSimulation.simulation.getRepaymentConfigAtDate(new DateOnly()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const defaultValues = useMemo(() => {
    return {
      frequency:
        frequencyOptions.find((opt) => opt.key !== currentPaymentConfig?.frequency)?.key ?? EPaymentFrequency.Monthly,
      firstPaymentDate: null,
    } as unknown as NewFrequencySchemaType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    control,
    watch,
    trigger,
    formState: { isValid, isValidating, isSubmitting },
  } = useForm({
    resolver,
    mode: 'onChange',
    defaultValues,
    context: { simulation: initialSignedSimulation.simulation },
  })

  const originalSimulation = initialSignedSimulation.simulation
  const { frequency, firstPaymentDate } = watch()

  const maxFirstPaymentDate = useMemo(
    () => getLoanCalendarMaxPaymentDate(originalSimulation, frequency, new DateOnly()),
    [originalSimulation, frequency],
  )

  useEffect(() => {
    // Forcing the trigger of validation for frequency when firstPaymentDate changes
    // sometimes the validation does not trigger automatically
    if (firstPaymentDate) void trigger('frequency')
  }, [firstPaymentDate, trigger])

  useEffect(() => {
    if (isValid && !isPending && !isValidating && !isSubmitting) {
      // Use setTimeout to defer the API call to the next tick
      // This is to ensure that the form state is fully updated before making the API call
      const timeoutId = setTimeout(() => {
        void computeAction({
          ...initialSignedSimulation,
          action: {
            actionType: ECalendarSimulationAction.UpdateFrequency,
            frequency,
            firstPaymentDate,
          },
        }).then((data) => {
          setNewSimulationWithSignature(data)
        })
      }, 0)

      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPaymentDate, frequency, isValid, isValidating])

  // Not using the handleSubmit to avoid unnecessary re-validation
  const onSubmitForm: FormEventHandler = (e) => {
    e.preventDefault()
    if (newSimulationWithSignature && !isPending) {
      onConfirm(newSimulationWithSignature)
      closeDialog()
    }
  }

  const shouldDisableDate = useShouldDisableDateForNewFirstPayment(originalSimulation, frequency)

  const newAmount = useMemo(() => {
    const paymentConfigs = newSimulationWithSignature?.simulation.fullLoan.paymentConfigs
    if (!paymentConfigs || paymentConfigs.length === 0) return undefined
    return paymentConfigs[paymentConfigs.length - 1].amount
  }, [newSimulationWithSignature?.simulation.fullLoan.paymentConfigs])

  return (
    <FormDialog
      {...dialogProps}
      onConfirm={onSubmitForm}
      title={t('loan.payment.editPaymentFrequency.title')}
      disabled={isPending || isLockedByOther}
    >
      {error && <ErrorAlert error={error} />}
      <Alert severity="info" sx={{ mb: 1 }}>
        {t('loan.payment.editPaymentFrequency.warningFirstPaymentDate')}
      </Alert>
      {maxFirstPaymentDate < minFirstPaymentDate && (
        <ErrorAlert error={t('loan.payment.editPaymentFrequency.errors.noValidDatesFound')} />
      )}

      <Stack spacing={2}>
        <LoanCalendarActionPreviewTable
          simulationBefore={originalSimulation}
          simulationAfter={newSimulationWithSignature.simulation}
          actionDate={firstPaymentDate}
        />
        <Stack direction="row" spacing={2}>
          <ControlledSelect
            name="frequency"
            control={control}
            options={frequencyOptions}
            label={t('loan.repaymentConfig.frequency')}
            disableEmptyOption
            disabled={isPending || isLockedByOther}
          />
          <ControlledDateOnlyPicker
            name="firstPaymentDate"
            control={control}
            label={t('loan.repaymentConfig.nextPaymentDate')}
            disabled={isPending || isLockedByOther}
            shouldDisableDate={shouldDisableDate}
            minDate={minFirstPaymentDate}
            maxDate={maxFirstPaymentDate}
          />
        </Stack>
        <Typography>
          <Trans i18nKey="loan.payment.editPaymentFrequency.newAmount" values={{ amount: formatCurrency(newAmount) }} />
        </Typography>
        <Alert severity="warning">{t('loan.payment.editPaymentFrequency.warning')}</Alert>
      </Stack>
    </FormDialog>
  )
}

export default LoanCalendarFrequencyDialog
